import { Table, TableBody, TableHead, TableRow, Typography, Chip, useTheme } from '@mui/material';
import { useAccountBalance } from '../../../hooks/useBankMovements';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  LoadingSkeleton,
} from '../../../common/components';
import { formatCurrency, formatAccountType, formatBankName } from '../utils/formatters';

/**
 * Componente para mostrar el resumen de todas las cuentas bancarias
 */
const AccountsOverview = ({ accounts, isLoading, onAccountSelect }) => {
  const theme = useTheme();
  const currentDate = new Date().toISOString().split('T')[0];

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const handleAccountClick = (account) => {
    onAccountSelect(account);
  };

  return (
    <GeneralTableBox>
      <Table>
        <TableHead>
          <TableRow>
            <HeadTableCell>Banco / Entidad</HeadTableCell>
            <HeadTableCell>Número cuenta</HeadTableCell>
            <HeadTableCell>Tipo cuenta</HeadTableCell>
            <HeadTableCell sx={{ textAlign: 'center' }}>Saldo Libro</HeadTableCell>
            <HeadTableCell sx={{ textAlign: 'center' }}>Saldo Real</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.map((account) => (
            <AccountRow
              key={account.uuid}
              account={account}
              currentDate={currentDate}
              onAccountClick={handleAccountClick}
              theme={theme}
            />
          ))}
          {accounts.length === 0 && (
            <TableRow>
              <RowTableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No hay cuentas bancarias disponibles
                </Typography>
              </RowTableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

/**
 * Componente para cada fila de cuenta bancaria
 */
const AccountRow = ({ account, currentDate, onAccountClick, theme }) => {
  const { data: balanceData, isLoading: balanceLoading } = useAccountBalance(
    account.uuid,
    currentDate
  );

  const bookBalance = balanceData?.book_balance || 0;
  const realBalance = balanceData?.real_balance || 0;
  const bookBalanceColor = bookBalance >= 0 ? theme.palette.success.main : theme.palette.error.main;
  const realBalanceColor = realBalance >= 0 ? theme.palette.success.main : theme.palette.error.main;

  return (
    <TableRow
      hover
      onClick={() => onAccountClick(account)}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
    >
      <RowTableCell>
        <Typography variant="body2" fontWeight="medium">
          {formatBankName(account.bank_name)}
        </Typography>
      </RowTableCell>

      <RowTableCell>
        <Typography variant="body2" fontFamily="monospace">
          {account.account_number}
        </Typography>
      </RowTableCell>

      <RowTableCell>
        <Chip
          label={formatAccountType(account.account_type)}
          size="small"
          variant="outlined"
          sx={{
            backgroundColor:
              account.account_type === 'CHECKING_ACCOUNT'
                ? theme.palette.primary.main
                : theme.palette.info.main,
            color: theme.palette.common.white,
          }}
        />
      </RowTableCell>

      <RowTableCell sx={{ textAlign: 'center' }}>
        <Typography variant="body2" fontWeight="medium" fontFamily="monospace" color={bookBalanceColor}>
          {balanceLoading ? '...' : formatCurrency(bookBalance, 2)}
        </Typography>
      </RowTableCell>

      <RowTableCell sx={{ textAlign: 'center' }}>
        <Typography
          variant="body2"
          fontWeight="medium"
          fontFamily="monospace"
          color={realBalanceColor}
        >
          {balanceLoading ? '...' : formatCurrency(realBalance, 2)}
        </Typography>
      </RowTableCell>
    </TableRow>
  );
};

export default AccountsOverview;
